import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useVideoEditor } from './context/VideoEditorContext';
import { PencilSquareIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

const MIN_DURATION = 1;
const DEFAULT_MAX_DURATION = 15; // 默认最大时长，会根据实际时长动态调整

export default function ImageDurationEditor() {
  const { t } = useTranslation(['editor', 'common']);
  const { state, actions } = useVideoEditor();
  const { selectedClip, editConfig } = state;

  const timelineRef = useRef<HTMLDivElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [tempDuration, setTempDuration] = useState(5);

  const clipEditInfo = editConfig?.clips.find(c => c.clipId === selectedClip?.id);
  const currentDuration = clipEditInfo?.duration || 5;

  // 动态计算最大时长：至少覆盖当前时长，且不低于默认最大值
  const MAX_DURATION = Math.max(DEFAULT_MAX_DURATION, Math.ceil(currentDuration));

  const displayDuration = isEditing ? tempDuration : currentDuration;

  const startEditing = () => {
    setIsEditing(true);
    setTempDuration(currentDuration);
  };

  const cancelEditing = () => {
    setIsEditing(false);
  };

  const applyDurationChange = () => {
    if (!editConfig || !clipEditInfo || !selectedClip) return;

    const oldDuration = currentDuration;
    const newDuration = tempDuration;
    const durationChange = newDuration - oldDuration;

    const updatedClips = editConfig.clips.map(c => {
      if (c.clipId === selectedClip.id) {
        return { ...c, duration: newDuration };
      }
      return c;
    });

    // 更新后续 clips 的 startTime
    const currentClipIndex = updatedClips.findIndex(c => c.clipId === selectedClip.id);
    for (let i = currentClipIndex + 1; i < updatedClips.length; i++) {
      updatedClips[i] = { ...updatedClips[i], startTime: updatedClips[i].startTime + durationChange };
    }

    const totalDuration = updatedClips.reduce((sum, clip) => sum + clip.duration, 0);
    const newConfig = {
      ...editConfig,
      clips: updatedClips,
      timeline: { ...editConfig.timeline, totalDuration }
    };

    actions.updateEditConfig(newConfig);
    setIsEditing(false);
  };

  const handleSliderDrag = (e: React.MouseEvent) => {
    if (!isEditing) return;
    e.preventDefault();
    e.stopPropagation();

    const updateDuration = (clientX: number) => {
      if (!timelineRef.current) return;
      const rect = timelineRef.current.getBoundingClientRect();
      const percent = (clientX - rect.left) / rect.width;
      const duration = MIN_DURATION + percent * (MAX_DURATION - MIN_DURATION);
      const clampedDuration = Math.max(MIN_DURATION, Math.min(MAX_DURATION, Math.round(duration * 10) / 10));
      setTempDuration(clampedDuration);
    };

    updateDuration(e.clientX);

    const handleMouseMove = (e: MouseEvent) => {
      updateDuration(e.clientX);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleTrackClick = (e: React.MouseEvent) => {
    if (!isEditing || !timelineRef.current) return;
    const rect = timelineRef.current.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const duration = MIN_DURATION + percent * (MAX_DURATION - MIN_DURATION);
    const clampedDuration = Math.max(MIN_DURATION, Math.min(MAX_DURATION, Math.round(duration * 10) / 10));
    setTempDuration(clampedDuration);
  };

  // 只要当前片段没有视频，就允许调整时间线中的片段时长
  if (!selectedClip || selectedClip.videoUrl) {
    return null;
  }

  const sliderPercent = ((displayDuration - MIN_DURATION) / (MAX_DURATION - MIN_DURATION)) * 100;

  return (
    <div className="flex items-center justify-center">
      <div className="w-full max-w-4xl bg-gray-800/50 backdrop-blur-sm rounded-lg px-6 py-3">
        <div className="flex items-center gap-4">
          {/* 时长标签 */}
          <span className="text-sm text-gray-400 whitespace-nowrap min-w-[60px]">
            {displayDuration.toFixed(1)}{t('editor:seconds')}
          </span>

          {/* 滑动条 */}
          <div className="flex-1">
            <div
              ref={timelineRef}
              className={`relative h-2 bg-gray-700 rounded-full ${isEditing ? 'cursor-pointer' : ''}`}
              onClick={handleTrackClick}
            >
              {/* 填充条 */}
              <div
                className={`absolute top-0 left-0 h-full rounded-full transition-all ${isEditing ? 'bg-green-500/80' : 'bg-gray-500/80'}`}
                style={{ width: `${sliderPercent}%` }}
              />
              {/* 滑块 */}
              <div
                className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full border-2 shadow-lg z-10 transition-all ${isEditing
                  ? 'border-green-500 cursor-grab active:cursor-grabbing hover:scale-110'
                  : 'border-gray-500 cursor-not-allowed opacity-50'
                  }`}
                style={{ left: `${sliderPercent}%`, transform: 'translate(-50%, -50%)' }}
                onMouseDown={handleSliderDrag}
              />
            </div>
            {/* 刻度标签 */}
            <div className="flex justify-between mt-1 text-xs text-gray-500">
              <span>{MIN_DURATION}s</span>
              <span>{MAX_DURATION}s</span>
            </div>
          </div>

          {/* 操作按钮 */}
          {!isEditing ? (
            <button
              onClick={startEditing}
              title={t('editor:editDuration')}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
            >
              <PencilSquareIcon className="w-5 h-5" />
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={cancelEditing}
                title={t('common:cancel')}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
              <button
                onClick={applyDurationChange}
                title={t('common:confirm')}
                className="p-2 text-green-400 hover:text-green-300 hover:bg-gray-700 rounded-lg transition-colors"
              >
                <CheckIcon className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
